import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Ban, CheckCircle2, Link2, RefreshCw, Unlink } from 'lucide-react';
import { Button } from '@components/common/Button';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { ReferenceDropdown } from './ReferenceDropdown';
import {
  useDocumentMatchDetails,
  usePurchaseOrderLines,
  useSubmitCorrections,
} from '@api/hooks/usePendingDocuments';
import { useAuthStore } from '@store/authStore';
import type { DocumentFieldsResponse } from '@schemas/api';

interface MatchingPanelProps {
  documentType: 'purchase_order' | 'receipt' | 'invoice';
  documentId?: string;
  documentData?: DocumentFieldsResponse;
  onRefresh?: () => Promise<void> | void;
}

const formatScore = (score?: number | null) => {
  if (score === null || score === undefined) return 'Not scored';
  const normalized = score > 1 ? score / 100 : score;
  return `${Math.round(normalized * 100)}%`;
};

const formatMoney = (value?: number | null, currency?: string | null) => {
  if (value === null || value === undefined) return '-';
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${currency || ''}`.trim();
};

export const MatchingPanel: React.FC<MatchingPanelProps> = ({
  documentType,
  documentId,
  documentData,
  onRefresh,
}) => {
  const currentUser = useAuthStore((state) => state.currentUser);
  const matchDetailsQuery = useDocumentMatchDetails(documentType, documentId);
  const submitCorrections = useSubmitCorrections();
  const details = matchDetailsQuery.data;
  const [selectedPoId, setSelectedPoId] = useState<string>('');
  const [selectedPoNumber, setSelectedPoNumber] = useState<string>('');

  useEffect(() => {
    const po = details?.linked_po || details?.selected_po;
    if (po?.id) {
      setSelectedPoId(String(po.id));
    }
    if (po?.po_number) {
      setSelectedPoNumber(String(po.po_number));
    }
  }, [details?.linked_po, details?.selected_po]);

  const poLinesQuery = usePurchaseOrderLines(selectedPoId || details?.linked_po?.id || null);
  const poLines = poLinesQuery.data || [];
  const documentLines = documentData?.lines || [];
  const memoryEvidence = details?.memory_evidence ?? [];
  const memorySuggested = memoryEvidence.length > 0;
  const linkedPo = details?.linked_po;
  const reasons = details?.reasons?.length ? details.reasons : [details?.summary?.detail].filter(Boolean) as string[];

  const refreshAll = useCallback(async () => {
    await matchDetailsQuery.refetch();
    await onRefresh?.();
  }, [matchDetailsQuery, onRefresh]);

  const submitMatchCorrection = useCallback(async (corrections: Record<string, any>, notes?: string) => {
    if (!documentId || !currentUser?.id || documentType === 'purchase_order') {
      return;
    }
    await submitCorrections.mutateAsync({
      documentType,
      documentId,
      user: currentUser.id,
      corrections,
      notes,
    });
    await refreshAll();
  }, [currentUser?.id, documentId, documentType, refreshAll, submitCorrections]);

  const confirmSelectedPo = useCallback(() => {
    if (!selectedPoNumber) return;
    submitMatchCorrection(
      { po_number: selectedPoNumber },
      memorySuggested ? 'Confirmed PO suggestion from matching panel' : 'Linked PO from matching panel',
    );
  }, [memorySuggested, selectedPoNumber, submitMatchCorrection]);

  const unlinkPo = useCallback(() => {
    submitMatchCorrection({ po_number: '' }, 'Unlinked PO from matching panel');
  }, [submitMatchCorrection]);

  const dontSuggestAgain = useCallback(() => {
    submitMatchCorrection({ po_number: '' }, 'Do not suggest this PO again');
  }, [submitMatchCorrection]);

  const updateLinePoLine = useCallback((line: NonNullable<DocumentFieldsResponse['lines']>[number], poLineId: string) => {
    submitMatchCorrection({
      lines: [
        {
          id: line.id,
          line_number: line.line_number,
          po_line_id: poLineId || null,
          po_number: poLineId ? selectedPoNumber || linkedPo?.po_number : '',
        },
      ],
    }, poLineId ? 'Changed PO line link from matching panel' : 'Unlinked PO line from matching panel');
  }, [linkedPo?.po_number, selectedPoNumber, submitMatchCorrection]);

  const topPoLabel = useMemo(() => {
    if (linkedPo?.po_number) return linkedPo.po_number;
    if (selectedPoNumber) return selectedPoNumber;
    return 'No PO selected';
  }, [linkedPo?.po_number, selectedPoNumber]);

  if (!documentId || documentType === 'purchase_order') {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm dark:border-gray-700 dark:bg-gray-900">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <Link2 className="h-4 w-4 text-blue-600 dark:text-blue-300" />
            <p className="font-semibold text-gray-900 dark:text-gray-100">{topPoLabel}</p>
            {memorySuggested ? (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-950 dark:text-blue-200">
                Suggested by confirmed matching history
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {linkedPo?.vendor_name || documentData?.vendor_name || documentData?.vendor_name_extracted || '-'} · {formatMoney(linkedPo?.total, linkedPo?.currency)}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Confidence</p>
          <p className="font-semibold text-gray-900 dark:text-gray-100">{formatScore(details?.confidence ?? details?.summary?.score)}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Reasons</p>
          <ul className="mt-2 space-y-1">
            {reasons.slice(0, 4).map((reason, index) => (
              <li key={`${reason}-${index}`} className="flex items-start gap-2 text-gray-700 dark:text-gray-300">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600 dark:text-green-300" />
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Next Action</p>
          <p className="mt-2 text-gray-700 dark:text-gray-300">{details?.next_action || 'Confirm or adjust match'}</p>
          {details?.waiting_for?.length ? (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Waiting for {details.waiting_for.join(', ')}
            </p>
          ) : null}
        </div>
      </div>

      {memorySuggested ? (
        <section className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/40">
          <p className="text-xs font-semibold uppercase tracking-wide text-blue-800 dark:text-blue-200">Why this was suggested</p>
          <ul className="mt-2 space-y-1.5 text-xs text-blue-900 dark:text-blue-100">
            {memoryEvidence.map((evidence, index) => (
              <li key={`${evidence.reason}-${index}`}>
                {evidence.summary}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-blue-800 dark:text-blue-200">
            Confirm if this is right, or choose a different PO/line. Your correction will guide future matching.
          </p>
        </section>
      ) : null}

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div className="min-w-[240px] flex-1">
          <p className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Linked PO</p>
          <ReferenceDropdown
            referenceType="purchase_order"
            value={selectedPoId}
            displayLabel={selectedPoNumber || linkedPo?.po_number || ''}
            onChange={(id, label) => {
              setSelectedPoId(String(id || ''));
              setSelectedPoNumber(label);
            }}
            placeholder="Choose PO..."
          />
        </div>
        <Button size="sm" onClick={confirmSelectedPo} disabled={!selectedPoNumber || submitCorrections.isPending}>
          <span className="inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" />
            Confirm
          </span>
        </Button>
        <Button size="sm" variant="secondary" onClick={unlinkPo} disabled={submitCorrections.isPending}>
          <span className="inline-flex items-center gap-1.5">
            <Unlink className="h-4 w-4" />
            Unlink
          </span>
        </Button>
        <Button size="sm" variant="secondary" onClick={dontSuggestAgain} disabled={submitCorrections.isPending}>
          <span className="inline-flex items-center gap-1.5">
            <Ban className="h-4 w-4" />
            Don't suggest again
          </span>
        </Button>
        <button
          type="button"
          onClick={refreshAll}
          className="rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100"
          title="Refresh match details"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {documentLines.length > 0 ? (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
              <tr>
                <th className="py-2 pr-3 font-medium">Line</th>
                <th className="py-2 pr-3 font-medium">Status</th>
                <th className="py-2 pr-3 font-medium">PO Line</th>
              </tr>
            </thead>
            <tbody>
              {documentLines.map((line, index) => (
                <tr key={line.id || index} className="border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                  <td className="py-2 pr-3 text-gray-700 dark:text-gray-300">
                    <span className="font-medium">{line.line_number ?? index + 1}</span>
                    <span className="ml-2 text-gray-500">{String(line.description || '').slice(0, 56)}</span>
                  </td>
                  <td className="py-2 pr-3 text-gray-600 dark:text-gray-300">
                    {line.manual_link ? 'Manual link' : (line.match_status || 'unmatched').replace(/_/g, ' ')}
                  </td>
                  <td className="py-2 pr-3">
                    <SearchableSelect
                      value={line.po_line_id || ''}
                      onChange={(selectedValue) => updateLinePoLine(line, selectedValue)}
                      disabled={poLinesQuery.isLoading || submitCorrections.isPending}
                      options={[
                        { id: '', label: 'Unlinked' },
                        ...poLines.map((poLine) => ({
                          id: poLine.id,
                          label: `${poLine.line_number} · ${poLine.sku ? `${poLine.sku} · ` : ''}${poLine.description}`,
                        })),
                      ]}
                      placeholder="Unlinked"
                      clearable={false}
                      className="min-w-[220px]"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default MatchingPanel;
